import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Form, Button, Spinner, Alert, Badge } from 'react-bootstrap';
import { FaFileExcel, FaSearch, FaFilter, FaCalendarAlt } from 'react-icons/fa';
import * as XLSX from 'xlsx';
import pythonApiClient from '../../../services/api/pythonBaseApi';

/** Coherente con backend: reserva pendiente de check-in y fila vigente (activo=1). */
function filaEsReservaActiva(r) {
  const flag = r.es_reserva_activa;
  const porFlag =
    flag === true ||
    flag === 1 ||
    flag === '1' ||
    (typeof flag === 'string' && flag.trim() === '1');
  const act = r.activo;
  const activoOk =
    act === true ||
    act === 1 ||
    act === '1' ||
    (typeof act === 'number' && Number(act) === 1);
  return porFlag && activoOk;
}

const ReporteReservas = () => {
  const selectedProject = JSON.parse(localStorage.getItem('selectedProject') || 'null');

  const [filas, setFilas] = useState([]);
  const [estadisticas, setEstadisticas] = useState(null);
  const [fechaRefPeru, setFechaRefPeru] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [zonas, setZonas] = useState([]);
  const [modulos, setModulos] = useState([]);
  const [filtros, setFiltros] = useState({
    alcance: 'todas',
    zona_id: '',
    modulo_id: '',
  });

  const loadReporte = async (filtrosOverride = null) => {
    if (!selectedProject?.id) return;

    const f = filtrosOverride || filtros;

    setLoading(true);
    setError(null);
    try {
      const params = { alcance: f.alcance || 'todas' };
      if (f.zona_id) params.zona_id = f.zona_id;
      if (f.modulo_id) params.modulo_id = f.modulo_id;

      const response = await pythonApiClient.get('/reportes/hoteleria/reservas/', { params });

      if (response.data.success) {
        let rows = response.data.data || [];
        const alc = f.alcance || 'todas';
        if (alc === 'activas') {
          rows = rows.filter(filaEsReservaActiva);
        }
        setFilas(rows);
        const est = response.data.estadisticas || null;
        if (est && alc === 'activas') {
          setEstadisticas({
            ...est,
            total_registros: rows.length,
            reservas_activas_en_resultado: rows.length,
          });
        } else {
          setEstadisticas(est);
        }
        setFechaRefPeru(response.data.fecha_referencia_peru || null);
      } else {
        setError(response.data.message || 'No se pudo cargar el reporte');
      }
    } catch (err) {
      console.error('Error cargando reporte reservas:', err);
      setError('No se pudo conectar con el servidor');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedProject?.id) {
      loadZonas();
      loadReporte();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (filtros.zona_id) {
      loadModulos(filtros.zona_id);
    } else {
      setModulos([]);
      setFiltros((prev) => ({ ...prev, modulo_id: '' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtros.zona_id]);

  const loadZonas = async () => {
    if (!selectedProject?.id) return;
    try {
      const response = await pythonApiClient.get('/mantenimientos/zonas', {
        params: { proyecto_id: selectedProject.id },
        headers: { 'X-Proyecto-ID': selectedProject.id },
      });
      setZonas(response.data || []);
    } catch (err) {
      console.error('Error cargando zonas:', err);
    }
  };

  const loadModulos = async (zonaId) => {
    try {
      const response = await pythonApiClient.get('/mantenimientos/modulos', {
        params: { zona_id: zonaId },
        headers: { 'X-Proyecto-ID': selectedProject.id },
      });
      setModulos(response.data || []);
    } catch (err) {
      console.error('Error cargando módulos:', err);
    }
  };

  const handleFilterChange = (field, value) => {
    setFiltros((prev) => ({ ...prev, [field]: value }));
  };

  const handleLimpiarFiltros = () => {
    const limpio = {
      alcance: 'todas',
      zona_id: '',
      modulo_id: '',
    };
    setFiltros(limpio);
    loadReporte(limpio);
  };

  const exportToExcel = () => {
    const dataToExport = filas.map((item) => ({
      ID: item.id,
      'Reserva activa': item.es_reserva_activa ? 'Sí' : 'No',
      DNI: item.dni,
      Trabajador: item.trabajador,
      Cargo: item.cargo || '-',
      Zona: item.zona,
      Módulo: item.modulo,
      Habitación: item.habitacion,
      Cama: item.cama,
      'Estado asignación': item.estado_asignacion || '-',
      'Fecha reserva': item.fecha_reserva_formateada || '-',
      'Días desde reserva': item.dias_desde_reserva,
      'Fecha referencia (PE)': fechaRefPeru || '-',
      'Check-in': item.fecha_check_in_formateada || '-',
      'Check-out': item.fecha_check_out_formateada || '-',
    }));

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Reservas');
    XLSX.writeFile(wb, `Reporte_Reservas_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const badgeDiasVariant = (d) => {
    if (d == null || Number.isNaN(d)) return 'secondary';
    if (d <= 2) return 'success';
    if (d <= 7) return 'warning';
    return 'danger';
  };

  if (!selectedProject) {
    return (
      <div className="dh-reporte-bg">
        <Container className="p-5">
          <Alert variant="warning">Por favor seleccione un proyecto para ver el reporte.</Alert>
        </Container>
      </div>
    );
  }

  return (
    <>
      <style>
        {`
          .dh-reporte-bg {
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            min-height: 100vh;
            padding: 2rem 0;
          }
          .dh-card {
            border: none;
            border-radius: 20px;
            box-shadow: 0 15px 35px rgba(0,0,0,0.1);
            background: white;
            margin-bottom: 2rem;
          }
          .dh-card-header {
            border-radius: 20px 20px 0 0;
            padding: 1.25rem 1.5rem;
            border: none;
            font-weight: 700;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
          }
          .btn-dh-primary {
            background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
            border: none;
            color: white;
            border-radius: 10px;
            padding: 10px 20px;
            font-weight: 600;
            font-size: 0.9rem;
          }
          .btn-dh-primary:hover { color: white; filter: brightness(1.05); }
          .btn-dh-secondary {
            background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%);
            border: none;
            color: white;
            border-radius: 10px;
            padding: 10px 20px;
            font-weight: 600;
            font-size: 0.9rem;
          }
          .btn-dh-secondary:hover { color: white; }
          .btn-dh-success {
            background: linear-gradient(135deg, #10b981 0%, #059669 100%);
            border: none;
            color: white;
            border-radius: 10px;
            padding: 10px 20px;
            font-weight: 600;
            font-size: 0.9rem;
          }
          .btn-dh-success:hover { color: white; filter: brightness(1.05); }
          .dh-form-control {
            border: 2px solid #e5e7eb;
            border-radius: 10px;
            padding: 12px 16px;
            font-size: 0.95rem;
          }
          .dh-form-control:focus {
            border-color: #667eea;
            box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
          }
          .dh-form-label {
            color: #374151;
            font-weight: 600;
            font-size: 0.95rem;
            margin-bottom: 8px;
          }
          .dh-table thead {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
          }
          .dh-table thead th {
            border: none;
            padding: 12px 14px;
            font-weight: 600;
            font-size: 0.8rem;
            text-transform: uppercase;
          }
          .dh-table tbody td {
            padding: 10px 14px;
            vertical-align: middle;
            font-size: 0.9rem;
          }
          .dh-stat-card {
            border-radius: 15px;
            padding: 1.25rem;
            box-shadow: 0 4px 12px rgba(0,0,0,0.08);
          }
        `}
      </style>

      <div className="dh-reporte-bg">
        <Container fluid className="px-4">
          <Card className="dh-card">
            <Card.Header className="dh-card-header">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <div className="d-flex align-items-center">
                  <FaCalendarAlt className="me-3" size={24} />
                  <div>
                    <h4 className="mb-0">Reporte de reservas</h4>
                    <small className="opacity-75 d-block">
                      Proyecto: {selectedProject.nombre}
                      {fechaRefPeru ? (
                        <span className="ms-2">
                          · Fecha actual (Perú) para cálculo de días:{' '}
                          <strong>{fechaRefPeru}</strong>
                        </span>
                      ) : null}
                    </small>
                  </div>
                </div>
                <Button
                  className="btn-dh-success"
                  onClick={exportToExcel}
                  disabled={loading || filas.length === 0}
                >
                  <FaFileExcel className="me-2" />
                  Exportar Excel ({filas.length})
                </Button>
              </div>
            </Card.Header>
            <Card.Body className="p-4">
              <Card className="mb-4" style={{ border: '2px solid #e5e7eb', borderRadius: '15px' }}>
                <Card.Body>
                  <Row className="align-items-end g-2">
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label className="dh-form-label">Tipo de reserva</Form.Label>
                        <Form.Select
                          className="dh-form-control"
                          value={filtros.alcance}
                          onChange={(e) => {
                            const v = e.target.value;
                            const next = { ...filtros, alcance: v };
                            setFiltros(next);
                            loadReporte(next);
                          }}
                        >
                          <option value="todas">Todas</option>
                          <option value="activas">Solo activas (pendientes de check-in)</option>
                          <option value="inactivas">No activas (check-in, finalizadas, etc.)</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={2}>
                      <Form.Group>
                        <Form.Label className="dh-form-label">Zona</Form.Label>
                        <Form.Select
                          className="dh-form-control"
                          value={filtros.zona_id}
                          onChange={(e) => handleFilterChange('zona_id', e.target.value)}
                        >
                          <option value="">Todas</option>
                          {zonas.map((zona) => (
                            <option key={zona.id} value={zona.id}>
                              {zona.nombre}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={2}>
                      <Form.Group>
                        <Form.Label className="dh-form-label">Módulo</Form.Label>
                        <Form.Select
                          className="dh-form-control"
                          value={filtros.modulo_id}
                          onChange={(e) => handleFilterChange('modulo_id', e.target.value)}
                          disabled={!filtros.zona_id}
                        >
                          <option value="">Todos</option>
                          {modulos.map((modulo) => (
                            <option key={modulo.id} value={modulo.id}>
                              {modulo.nombre}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <div className="small text-muted mb-1">
                        Los <strong>días desde la reserva</strong> son: fecha actual en Perú − fecha en que se
                        registró la reserva (solo la parte fecha).
                      </div>
                    </Col>
                    <Col md={2}>
                      <div className="d-flex flex-column gap-2">
                        <Button className="btn-dh-primary w-100" onClick={loadReporte} disabled={loading}>
                          <FaSearch className="me-2" />
                          Buscar
                        </Button>
                        <Button className="btn-dh-secondary w-100" onClick={handleLimpiarFiltros} disabled={loading}>
                          <FaFilter className="me-2" />
                          Limpiar
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {error && (
                <Alert variant="danger" onClose={() => setError(null)} dismissible>
                  {error}
                </Alert>
              )}

              {estadisticas && (
                <Row className="mb-4 g-2">
                  <Col md={4}>
                    <Card className="dh-stat-card text-center border-0 bg-light">
                      <Card.Body>
                        <h3 className="text-primary mb-0">{estadisticas.total_registros}</h3>
                        <small className="text-muted fw-bold">Registros en pantalla</small>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={4}>
                    <Card className="dh-stat-card text-center border-0 bg-light">
                      <Card.Body>
                        <h3 className="text-success mb-0">{estadisticas.reservas_activas_en_resultado}</h3>
                        <small className="text-muted fw-bold">Reservas activas en el resultado</small>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              )}

              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" className="me-2" />
                  Cargando…
                </div>
              ) : (
                <div className="table-responsive">
                  <Table hover className="dh-table">
                    <thead>
                      <tr>
                        <th>Activa</th>
                        <th>Días desde reserva</th>
                        <th>DNI</th>
                        <th>Trabajador</th>
                        <th>Zona</th>
                        <th>Módulo</th>
                        <th>Hab.</th>
                        <th>Cama</th>
                        <th>Estado</th>
                        <th>Fecha reserva</th>
                        <th>Check-in</th>
                        <th>Check-out</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filas.length === 0 ? (
                        <tr>
                          <td colSpan={12} className="text-center text-muted py-4">
                            No hay datos con los filtros seleccionados.
                          </td>
                        </tr>
                      ) : (
                        filas.map((row) => (
                          <tr key={row.id}>
                            <td>
                              {row.es_reserva_activa ? (
                                <Badge bg="success">Sí</Badge>
                              ) : (
                                <Badge bg="secondary">No</Badge>
                              )}
                            </td>
                            <td>
                              <Badge bg={badgeDiasVariant(row.dias_desde_reserva)}>
                                {row.dias_desde_reserva != null ? `${row.dias_desde_reserva} d` : '—'}
                              </Badge>
                            </td>
                            <td>{row.dni}</td>
                            <td className="fw-semibold">{row.trabajador}</td>
                            <td>{row.zona}</td>
                            <td>{row.modulo}</td>
                            <td>{row.habitacion}</td>
                            <td>{row.cama}</td>
                            <td>{row.estado_asignacion || '—'}</td>
                            <td className="text-nowrap small">{row.fecha_reserva_formateada || '—'}</td>
                            <td className="text-nowrap small">{row.fecha_check_in_formateada || '—'}</td>
                            <td className="text-nowrap small">{row.fecha_check_out_formateada || '—'}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Container>
      </div>
    </>
  );
};

export default ReporteReservas;
